import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Undo2 } from 'lucide-react';
import AinaaEmblem from '@/components/AinaaEmblem';
import { useCurrency } from '@/context/CurrencyContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const { country, lang, updateCountry, updateLang, CURRENCIES } = useCurrency();

  const [portalLoggedIn, setPortalLoggedIn] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);

  useEffect(() => {
    setPortalLoggedIn(localStorage.getItem('ainaa_portal_logged_in') === 'true');
    setAdminLoggedIn(localStorage.getItem('ainaa_admin_logged_in') === 'true');
  }, [location.pathname]);

  const t = (en, hi) => lang === 'hi' ? hi : en;

  return (
    <footer className="border-t-2 border-[#0F1B2D] bg-[#0F1B2D]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-4">
              <AinaaEmblem size={36} variant="inverted" />
              <div className="flex flex-col leading-none">
                <span className="font-['Playfair_Display'] text-lg font-semibold tracking-tight text-[#F5F3EE]">
                  AINAA
                </span>
                <span className="text-[7px] uppercase tracking-[0.15em] text-[#B0ACA4] font-semibold leading-tight block max-w-[200px]">
                  {t('Artist Identification Network & Accreditation Archive', 'कलाकार पहचान नेटवर्क और प्रत्यायन संग्रह')}
                </span>
              </div>
            </Link>
            <p className="text-[#B0ACA4] text-xs font-medium leading-relaxed max-w-xs">
              {t(
                'The professional association for artists. Credibility verification, legal aid, financial support, and behavioral intelligence.',
                'कलाकारों का पेशेवर संघ। विश्वसनीयता सत्यापन, कानूनी सहायता, वित्तीय सहायता, और व्यवहार बुद्धिमत्ता।'
              )}
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-[#B0ACA4] font-bold mb-3">
              {t('Services', 'सेवाएँ')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/track" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-track-link">
                  {t('Track Application', 'आवेदन ट्रैक करें')}
                </Link>
              </li>
              <li>
                <Link to="/check-score" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-check-score-link">
                  {t('Check Score', 'स्कोर जाँचें')}
                </Link>
              </li>
              <li>
                <Link to="/registry" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-registry-link">
                  {t('Registry', 'रजिस्ट्री')}
                </Link>
              </li>
              <li>
                {portalLoggedIn ? (
                  <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C4A35A] hover:text-[#F5F3EE] transition-colors" data-testid="footer-back-portal-link">
                    <Undo2 size={12} />
                    {t('Back to Portal', 'पोर्टल पर वापस')}
                  </Link>
                ) : (
                  <Link to="/portal" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-portal-link">
                    {t('Member Portal', 'सदस्य पोर्टल')}
                  </Link>
                )}
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-[#B0ACA4] font-bold mb-3">
              {t('Legal', 'कानूनी')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-privacy-link">
                  {t('Privacy Policy', 'गोपनीयता नीति')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-terms-link">
                  {t('Terms of Service', 'सेवा की शर्तें')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-[#B0ACA4] font-bold mb-3">
              {t('Contact', 'संपर्क')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-contact-link">
                  {t('Connect with us', 'हमसे जुड़ें')}
                </Link>
              </li>
              <li>
                <a href="mailto:info@ainaa.co" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors">
                  info@ainaa.co
                </a>
              </li>
              <li className="pt-1">
                {adminLoggedIn ? (
                  <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C4A35A] hover:text-[#F5F3EE] transition-colors" data-testid="footer-back-console-link">
                    <Undo2 size={12} />
                    {t('Back to Console', 'कंसोल पर वापस')}
                  </Link>
                ) : (
                  <Link to="/admin" className="text-xs font-medium text-[#C8C4BC] hover:text-[#F5F3EE] transition-colors" data-testid="footer-admin-link">
                    {t('Admin Console', 'एडमिन कंसोल')}
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-4 border-t border-[#1A2B45] flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-[10px] font-medium text-[#9B9589]">
            &copy; {currentYear} AINAA. {t('All rights reserved.', 'सर्वाधिकार सुरक्षित।')}
          </p>
          <div className="flex items-center gap-3">
            {/* Country + Currency selector */}
            <select
              value={country}
              onChange={(e) => {
                updateCountry(e.target.value);
                window.dispatchEvent(new Event('ainaa-country-change'));
              }}
              className="bg-[#1A2B45] border border-[#2A3B55] text-[10px] text-[#B0ACA4] px-2 py-1 font-medium outline-none cursor-pointer hover:border-[#C4A35A] transition-colors"
              data-testid="footer-country-selector"
            >
              {Object.entries(CURRENCIES).map(([code, c]) => (
                <option key={code} value={code}>{c.flag} {code} — {c.code}</option>
              ))}
            </select>

            {/* Language toggle */}
            <button
              onClick={() => {
                const next = lang === 'en' ? 'hi' : 'en';
                updateLang(next);
                window.dispatchEvent(new Event('ainaa-lang-change'));
              }}
              className="bg-[#1A2B45] border border-[#2A3B55] text-[10px] text-[#B0ACA4] px-2 py-1 font-medium cursor-pointer hover:border-[#C4A35A] transition-colors"
              data-testid="footer-lang-toggle"
            >
              {lang === 'en' ? 'हिन्दी' : 'English'}
            </button>

            <p className="text-[10px] font-medium text-[#9B9589]">
              {t('For creative professionals worldwide.', 'दुनिया भर के रचनात्मक पेशेवरों के लिए।')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
